import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
    FileText, Download, Calendar as CalendarIcon, Filter, 
    FileSpreadsheet, FileJson, RefreshCw, Clock, BarChart3,
    TrendingUp, Zap, Activity, Euro
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const PERIOD_OPTIONS = [
    { value: 'today', label: 'Hoje', groupings: ['15min', 'hour'] },
    { value: 'week', label: 'Última Semana', groupings: ['day'] },
    { value: 'month', label: 'Este Mês', groupings: ['day', 'week'] },
    { value: '6months', label: 'Últimos 6 Meses', groupings: ['week', 'month'] },
    { value: 'year', label: 'Último Ano', groupings: ['month'] },
    { value: 'custom', label: 'Personalizado', groupings: ['day', 'week', 'month'] }
];

const GROUPING_LABELS = {
    '15min': '15 minutos',
    'hour': 'Hora',
    'day': 'Dia',
    'week': 'Semana',
    'month': 'Mês'
};

export default function Records() {
    const { api, isAdmin } = useAuth();
    const [aggregatedData, setAggregatedData] = useState(null);
    const [rawReadings, setRawReadings] = useState([]);
    const [devices, setDevices] = useState([]);
    const [areas, setAreas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [collecting, setCollecting] = useState(false);
    const [collectionStatus, setCollectionStatus] = useState(null);
    const [viewMode, setViewMode] = useState('aggregated'); // 'aggregated' or 'raw'
    
    const [filters, setFilters] = useState({
        period: 'today',
        grouping: 'hour',
        device_id: '',
        area_id: '',
        measurement_type: '',
        start_date: subDays(new Date(), 7),
        end_date: new Date()
    });
    
    const [showStartCalendar, setShowStartCalendar] = useState(false);
    const [showEndCalendar, setShowEndCalendar] = useState(false);
    const [showMaxCurrent, setShowMaxCurrent] = useState(false);

    // Get available groupings for selected period
    const getAvailableGroupings = useCallback(() => {
        const period = PERIOD_OPTIONS.find(p => p.value === filters.period);
        return period?.groupings || ['day'];
    }, [filters.period]);

    // Update grouping when period changes
    useEffect(() => {
        const available = getAvailableGroupings();
        if (!available.includes(filters.grouping)) {
            setFilters(prev => ({ ...prev, grouping: available[0] }));
        }
    }, [filters.period, getAvailableGroupings]);

    const fetchFiltersData = async () => {
        try {
            const [devicesRes, areasRes] = await Promise.all([
                api.get('/devices'),
                api.get('/areas')
            ]);
            setDevices(devicesRes.data);
            setAreas(areasRes.data);
        } catch (error) {
            console.error('Error fetching filter data:', error);
        }
    };

    const fetchCollectionStatus = async () => {
        try {
            const response = await api.get('/readings/collection-status');
            setCollectionStatus(response.data);
        } catch (error) {
            console.error('Error fetching collection status:', error);
        }
    };

    const fetchAggregatedReadings = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('period', filters.period);
            params.append('grouping', filters.grouping);
            
            if (filters.device_id) params.append('device_id', filters.device_id);
            if (filters.area_id) params.append('area_id', filters.area_id);
            if (filters.measurement_type) params.append('measurement_type', filters.measurement_type);
            
            if (filters.period === 'custom') {
                params.append('start_date', filters.start_date.toISOString());
                params.append('end_date', filters.end_date.toISOString());
            }
            
            const response = await api.get(`/readings/aggregated?${params.toString()}`);
            setAggregatedData(response.data);
        } catch (error) {
            console.error('Error fetching aggregated readings:', error);
            toast.error('Erro ao carregar registos agregados');
        } finally {
            setLoading(false);
        }
    };

    const fetchRawReadings = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            
            if (filters.device_id) params.append('device_id', filters.device_id);
            if (filters.area_id) params.append('area_id', filters.area_id);
            if (filters.measurement_type) params.append('measurement_type', filters.measurement_type);
            
            let startDate = filters.start_date;
            let endDate = filters.end_date;
            
            switch (filters.period) {
                case 'today':
                    startDate = new Date();
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date();
                    break;
                case 'week':
                    startDate = subDays(new Date(), 7);
                    endDate = new Date();
                    break;
                case 'month':
                    startDate = startOfMonth(new Date());
                    endDate = endOfMonth(new Date());
                    break;
                case '6months':
                    startDate = subMonths(new Date(), 6);
                    endDate = new Date();
                    break;
                case 'year':
                    startDate = subMonths(new Date(), 12);
                    endDate = new Date();
                    break;
                default:
                    break;
            }
            
            params.append('start_date', startDate.toISOString());
            params.append('end_date', endDate.toISOString());
            params.append('limit', '500');
            
            const response = await api.get(`/readings?${params.toString()}`);
            setRawReadings(response.data);
        } catch (error) {
            console.error('Error fetching raw readings:', error);
            toast.error('Erro ao carregar registos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiltersData();
        fetchCollectionStatus();
    }, []);

    useEffect(() => {
        if (viewMode === 'aggregated') {
            fetchAggregatedReadings();
        } else {
            fetchRawReadings();
        }
    }, [filters, viewMode]);

    const handleCollectData = async () => {
        setCollecting(true);
        try {
            await api.post('/readings/collect');
            toast.success('Recolha de dados iniciada');
            // Refresh after a short delay
            setTimeout(() => {
                fetchAggregatedReadings();
                fetchCollectionStatus();
            }, 3000);
        } catch (error) {
            console.error('Error collecting data:', error);
            toast.error(error.response?.data?.detail || 'Erro ao iniciar recolha');
        } finally {
            setCollecting(false);
        }
    };

    const handleExport = async (exportFormat) => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            params.append('format', exportFormat);
            
            if (filters.device_id) params.append('device_id', filters.device_id);
            if (filters.area_id) params.append('area_id', filters.area_id);
            if (filters.measurement_type) params.append('measurement_type', filters.measurement_type);
            
            let startDate = filters.start_date;
            let endDate = filters.end_date;
            
            switch (filters.period) {
                case 'today':
                    startDate = new Date();
                    startDate.setHours(0, 0, 0, 0);
                    endDate = new Date();
                    break;
                case 'week':
                    startDate = subDays(new Date(), 7);
                    endDate = new Date();
                    break;
                case 'month':
                    startDate = startOfMonth(new Date());
                    endDate = endOfMonth(new Date());
                    break;
                case '6months':
                    startDate = subMonths(new Date(), 6);
                    endDate = new Date();
                    break;
                case 'year':
                    startDate = subMonths(new Date(), 12);
                    endDate = new Date();
                    break;
                default:
                    break;
            }
            
            params.append('start_date', startDate.toISOString());
            params.append('end_date', endDate.toISOString());
            
            const response = await api.get(`/readings/export?${params.toString()}`, {
                responseType: 'blob'
            });
            
            const blob = new Blob([response.data], {
                type: exportFormat === 'csv' ? 'text/csv' : 'application/json'
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `energy_readings_${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            toast.success(`Exportação ${exportFormat.toUpperCase()} concluída`);
        } catch (error) {
            console.error('Error exporting:', error);
            toast.error('Erro ao exportar registos');
        } finally {
            setExporting(false);
        }
    };

    const formatPower = (value) => {
        if (value >= 1000) return `${(value / 1000).toFixed(2)} kW`;
        return `${value?.toFixed(0) || 0} W`;
    };

    const formatDate = (dateString) => {
        return format(new Date(dateString), "dd/MM/yyyy HH:mm", { locale: pt });
    };

    const formatLastReading = (timestamp) => {
        if (!timestamp) return 'Nunca';
        return format(new Date(timestamp), "dd/MM HH:mm", { locale: pt });
    };

    // Chart data preparation
    const chartData = aggregatedData?.readings?.map(r => ({
        name: r.period_label,
        potencia: Math.round(r.avg_power),
        maxPotencia: Math.round(r.max_power),
        energia: r.total_energy
    })) || [];

    return (
        <div className="space-y-6" data-testid="records-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold font-heading text-slate-900">Registos de Energia</h1>
                    <p className="text-slate-500">Visualize e analise o histórico de leituras</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {isAdmin && (
                        <Button
                            variant="outline"
                            onClick={handleCollectData}
                            disabled={collecting}
                            data-testid="collect-data-btn"
                        >
                            <RefreshCw className={`w-4 h-4 mr-2 ${collecting ? 'animate-spin' : ''}`} />
                            {collecting ? 'A recolher...' : 'Recolher Agora'}
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        onClick={() => handleExport('csv')}
                        disabled={exporting}
                        data-testid="export-csv-btn"
                    >
                        <FileSpreadsheet className="w-4 h-4 mr-2" />
                        CSV
                    </Button>
                    <Button
                        variant="outline"
                        onClick={() => handleExport('json')}
                        disabled={exporting}
                        data-testid="export-json-btn"
                    >
                        <FileJson className="w-4 h-4 mr-2" />
                        JSON
                    </Button>
                </div>
            </div>

            {/* Collection Status */}
            {collectionStatus && (
                <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="pt-4">
                        <div className="flex flex-wrap items-center gap-6 text-sm">
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">
                                    Recolha automática: <strong>cada {collectionStatus.collection_interval_minutes} minutos</strong>
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Activity className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">
                                    Última leitura: <strong>{formatLastReading(collectionStatus.last_reading_timestamp)}</strong>
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <BarChart3 className="w-4 h-4 text-slate-500" />
                                <span className="text-slate-600">
                                    Registos hoje: <strong>{collectionStatus.readings_today}</strong>
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Filters */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        Filtros
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
                        {/* Period */}
                        <div className="space-y-2">
                            <Label>Período</Label>
                            <Select
                                value={filters.period}
                                onValueChange={(value) => setFilters({ ...filters, period: value })}
                            >
                                <SelectTrigger data-testid="period-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PERIOD_OPTIONS.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Grouping */}
                        <div className="space-y-2">
                            <Label>Agrupar por</Label>
                            <Select
                                value={filters.grouping}
                                onValueChange={(value) => setFilters({ ...filters, grouping: value })}
                            >
                                <SelectTrigger data-testid="grouping-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {getAvailableGroupings().map((g) => (
                                        <SelectItem key={g} value={g}>
                                            {GROUPING_LABELS[g]}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Custom Date Range */}
                        {filters.period === 'custom' && (
                            <>
                                <div className="space-y-2">
                                    <Label>Data Início</Label>
                                    <Popover open={showStartCalendar} onOpenChange={setShowStartCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start">
                                                <CalendarIcon className="w-4 h-4 mr-2" />
                                                {format(filters.start_date, "dd/MM/yyyy", { locale: pt })}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={filters.start_date}
                                                onSelect={(date) => {
                                                    setFilters({ ...filters, start_date: date || new Date() });
                                                    setShowStartCalendar(false);
                                                }}
                                                locale={pt}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div className="space-y-2">
                                    <Label>Data Fim</Label>
                                    <Popover open={showEndCalendar} onOpenChange={setShowEndCalendar}>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start">
                                                <CalendarIcon className="w-4 h-4 mr-2" />
                                                {format(filters.end_date, "dd/MM/yyyy", { locale: pt })}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={filters.end_date}
                                                onSelect={(date) => {
                                                    setFilters({ ...filters, end_date: date || new Date() });
                                                    setShowEndCalendar(false);
                                                }}
                                                locale={pt}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                            </>
                        )}

                        {/* Device Filter */}
                        <div className="space-y-2">
                            <Label>Dispositivo</Label>
                            <Select
                                value={filters.device_id || "all"}
                                onValueChange={(value) => setFilters({ ...filters, device_id: value === "all" ? "" : value })}
                            >
                                <SelectTrigger data-testid="device-filter-select">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    {devices.map((device) => (
                                        <SelectItem key={device.id} value={device.id}>
                                            {device.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Area Filter */}
                        <div className="space-y-2">
                            <Label>Área</Label>
                            <Select
                                value={filters.area_id || "all"}
                                onValueChange={(value) => setFilters({ ...filters, area_id: value === "all" ? "" : value })}
                            >
                                <SelectTrigger data-testid="area-filter-select">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todas</SelectItem>
                                    {areas.map((area) => (
                                        <SelectItem key={area.id} value={area.id}>
                                            {area.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Measurement Type Filter */}
                        <div className="space-y-2">
                            <Label>Tipo</Label>
                            <Select
                                value={filters.measurement_type || "all"}
                                onValueChange={(value) => setFilters({ ...filters, measurement_type: value === "all" ? "" : value })}
                            >
                                <SelectTrigger data-testid="type-filter-select">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos</SelectItem>
                                    <SelectItem value="consumption">Consumo</SelectItem>
                                    <SelectItem value="production">Produção</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Summary Cards */}
            {aggregatedData && viewMode === 'aggregated' && (
                <div className={`grid gap-4 ${aggregatedData.energy_price ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-50">
                                    <Zap className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Energia Total</p>
                                    <p className="text-xl font-bold text-slate-900">{aggregatedData.total_energy.toFixed(2)} kWh</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-emerald-50">
                                    <Activity className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Potência Média</p>
                                    <p className="text-xl font-bold text-slate-900">{formatPower(aggregatedData.avg_power)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-orange-50">
                                    <TrendingUp className="w-5 h-5 text-orange-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Potência Máxima</p>
                                    <p className="text-xl font-bold text-slate-900">{formatPower(aggregatedData.max_power)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-purple-50">
                                    <BarChart3 className="w-5 h-5 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Total Registos</p>
                                    <p className="text-xl font-bold text-slate-900">{aggregatedData.total_readings}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    {aggregatedData.energy_price && (
                        <Card className="border-l-4 border-l-amber-500">
                            <CardContent className="pt-4">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 rounded-lg bg-amber-50">
                                        <Euro className="w-5 h-5 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-sm text-slate-500">Custo Total</p>
                                        <p className="text-xl font-bold text-amber-600">{aggregatedData.total_cost?.toFixed(2)} €</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Chart */}
            {aggregatedData && viewMode === 'aggregated' && chartData.length > 0 && (
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-base font-heading">Evolução da Potência</CardTitle>
                        <CardDescription>
                            Agrupado por {GROUPING_LABELS[filters.grouping].toLowerCase()}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="h-[300px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis 
                                        dataKey="name" 
                                        tick={{ fontSize: 12 }}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        tick={{ fontSize: 12 }}
                                        tickLine={false}
                                        tickFormatter={(value) => `${value}W`}
                                    />
                                    <Tooltip 
                                        formatter={(value, name) => [
                                            `${value} W`,
                                            name
                                        ]}
                                        contentStyle={{
                                            backgroundColor: 'white',
                                            border: '1px solid #e2e8f0',
                                            borderRadius: '8px'
                                        }}
                                    />
                                    <Legend />
                                    <Bar 
                                        dataKey="potencia" 
                                        name="Potência Média"
                                        fill="#3b82f6" 
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar 
                                        dataKey="maxPotencia" 
                                        name="Potência Máxima"
                                        fill="#f59e0b" 
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* View Toggle and Table */}
            <Card>
                <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-heading">
                            Registos
                        </CardTitle>
                        <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
                            <TabsList className="h-8">
                                <TabsTrigger value="aggregated" className="text-xs px-3">
                                    Agregados
                                </TabsTrigger>
                                <TabsTrigger value="raw" className="text-xs px-3">
                                    Detalhados
                                </TabsTrigger>
                            </TabsList>
                        </Tabs>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-3" data-testid="records-loading">
                            {[...Array(10)].map((_, i) => (
                                <Skeleton key={i} className="h-12 w-full" />
                            ))}
                        </div>
                    ) : viewMode === 'aggregated' ? (
                        // Aggregated View
                        aggregatedData?.readings?.length === 0 ? (
                            <div className="text-center py-12" data-testid="no-records">
                                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                <h3 className="font-semibold text-slate-900 mb-2">Sem registos</h3>
                                <p className="text-slate-500">Não existem registos para os filtros selecionados</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="data-table-header">
                                            <TableHead>Período</TableHead>
                                            <TableHead className="text-right">Potência Média</TableHead>
                                            <TableHead className="text-right">Potência Máx</TableHead>
                                            <TableHead className="text-right">Potência Mín</TableHead>
                                            <TableHead className="text-right">Tensão Média</TableHead>
                                            <TableHead className="text-right">Energia</TableHead>
                                            {aggregatedData?.energy_price && (
                                                <TableHead className="text-right">Custo</TableHead>
                                            )}
                                            <TableHead className="text-right">Registos</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {aggregatedData?.readings?.map((reading, idx) => (
                                            <TableRow key={idx} className="hover:bg-slate-50">
                                                <TableCell className="font-medium">
                                                    {reading.period_label}
                                                </TableCell>
                                                <TableCell className="text-right font-semibold">
                                                    {formatPower(reading.avg_power)}
                                                </TableCell>
                                                <TableCell className="text-right text-orange-600">
                                                    {formatPower(reading.max_power)}
                                                </TableCell>
                                                <TableCell className="text-right text-blue-600">
                                                    {formatPower(reading.min_power)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {reading.avg_voltage?.toFixed(1)} V
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {reading.total_energy?.toFixed(2)} kWh
                                                </TableCell>
                                                {aggregatedData?.energy_price && (
                                                    <TableCell className="text-right text-amber-600 font-medium">
                                                        {reading.cost?.toFixed(2)} €
                                                    </TableCell>
                                                )}
                                                <TableCell className="text-right">
                                                    <Badge variant="secondary">{reading.reading_count}</Badge>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow className="bg-slate-100 font-semibold">
                                            <TableCell>TOTAL</TableCell>
                                            <TableCell className="text-right">
                                                {formatPower(aggregatedData?.avg_power || 0)}
                                            </TableCell>
                                            <TableCell className="text-right text-orange-600">
                                                {formatPower(aggregatedData?.max_power || 0)}
                                            </TableCell>
                                            <TableCell className="text-right">-</TableCell>
                                            <TableCell className="text-right">-</TableCell>
                                            <TableCell className="text-right font-bold">
                                                {aggregatedData?.total_energy?.toFixed(2)} kWh
                                            </TableCell>
                                            {aggregatedData?.energy_price && (
                                                <TableCell className="text-right text-amber-600 font-bold">
                                                    {aggregatedData?.total_cost?.toFixed(2)} €
                                                </TableCell>
                                            )}
                                            <TableCell className="text-right">
                                                <Badge variant="secondary">{aggregatedData?.total_readings}</Badge>
                                            </TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </div>
                        )
                    ) : (
                        // Raw View
                        rawReadings.length === 0 ? (
                            <div className="text-center py-12" data-testid="no-records">
                                <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                                <h3 className="font-semibold text-slate-900 mb-2">Sem registos</h3>
                                <p className="text-slate-500">Não existem registos para os filtros selecionados</p>
                            </div>
                        ) : (
                            <>
                            {/* Toggle for Current Display */}
                            <div className="flex items-center justify-end gap-2 mb-4 p-3 bg-slate-50 rounded-lg border">
                                <Label htmlFor="current-toggle" className="text-sm text-slate-600">
                                    Corrente Média
                                </Label>
                                <Switch
                                    id="current-toggle"
                                    checked={showMaxCurrent}
                                    onCheckedChange={setShowMaxCurrent}
                                    data-testid="current-toggle-switch"
                                />
                                <Label htmlFor="current-toggle" className={`text-sm ${showMaxCurrent ? 'text-orange-600 font-medium' : 'text-slate-600'}`}>
                                    Corrente Máxima
                                </Label>
                            </div>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="data-table-header">
                                            <TableHead>Data/Hora</TableHead>
                                            <TableHead>Dispositivo</TableHead>
                                            <TableHead>Área</TableHead>
                                            <TableHead>Tipo</TableHead>
                                            <TableHead className="text-right">Potência</TableHead>
                                            <TableHead className="text-right">Tensão</TableHead>
                                            <TableHead className={`text-right ${showMaxCurrent ? 'text-orange-600' : ''}`}>
                                                {showMaxCurrent ? 'Corrente Máx' : 'Corrente Média'}
                                            </TableHead>
                                            <TableHead className="text-right">Energia</TableHead>
                                            {rawReadings[0]?.cost !== undefined && rawReadings[0]?.cost !== null && (
                                                <TableHead className="text-right">Custo</TableHead>
                                            )}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {rawReadings.map((reading) => (
                                            <TableRow 
                                                key={reading.id} 
                                                className={`hover:bg-slate-50 ${reading.is_sub_meter ? 'bg-purple-50/50 text-purple-700' : ''}`}
                                                data-testid={`reading-row-${reading.id}`}
                                            >
                                                <TableCell className="font-mono text-sm">
                                                    {formatDate(reading.timestamp)}
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {reading.device_name || '-'}
                                                    {reading.is_sub_meter && (
                                                        <span className="ml-2 text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">
                                                            Sub-contagem
                                                        </span>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {reading.area_name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                        reading.measurement_type === 'production'
                                                            ? 'bg-emerald-100 text-emerald-700'
                                                            : reading.is_sub_meter
                                                                ? 'bg-purple-100 text-purple-700'
                                                                : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {reading.measurement_type === 'production' ? 'Produção' : 'Consumo'}
                                                    </span>
                                                </TableCell>
                                                <TableCell className={`text-right font-semibold ${reading.is_sub_meter ? 'text-purple-600' : ''}`}>
                                                    {formatPower(reading.power)}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {reading.voltage?.toFixed(1)} V
                                                </TableCell>
                                                <TableCell className={`text-right ${showMaxCurrent ? 'text-orange-600' : ''}`}>
                                                    {showMaxCurrent 
                                                        ? (reading.current_max?.toFixed(2) || reading.current?.toFixed(2)) 
                                                        : reading.current?.toFixed(2)} A
                                                </TableCell>
                                                <TableCell className={`text-right ${reading.is_sub_meter ? 'text-purple-600' : ''}`}>
                                                    {reading.energy?.toFixed(3)} kWh
                                                </TableCell>
                                                {rawReadings[0]?.cost !== undefined && rawReadings[0]?.cost !== null && (
                                                    <TableCell className={`text-right font-medium ${reading.is_sub_meter ? 'text-purple-600' : 'text-amber-600'}`}>
                                                        {reading.cost?.toFixed(4)} €
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow className="bg-slate-100 font-semibold">
                                            <TableCell colSpan={4}>
                                                TOTAL / MÉDIA
                                                <span className="ml-2 text-xs text-slate-500 font-normal">
                                                    (excluindo sub-contagens)
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatPower(rawReadings.filter(r => !r.is_sub_meter).reduce((sum, r) => sum + (r.power || 0), 0) / Math.max(rawReadings.filter(r => !r.is_sub_meter).length, 1))}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {(rawReadings.filter(r => !r.is_sub_meter).reduce((sum, r) => sum + (r.voltage || 0), 0) / Math.max(rawReadings.filter(r => !r.is_sub_meter).length, 1)).toFixed(1)} V
                                            </TableCell>
                                            <TableCell className={`text-right ${showMaxCurrent ? 'text-orange-600' : ''}`}>
                                                {showMaxCurrent 
                                                    ? (rawReadings.filter(r => !r.is_sub_meter).reduce((max, r) => Math.max(max, r.current_max || r.current || 0), 0)).toFixed(2)
                                                    : (rawReadings.filter(r => !r.is_sub_meter).reduce((sum, r) => sum + (r.current || 0), 0) / Math.max(rawReadings.filter(r => !r.is_sub_meter).length, 1)).toFixed(2)} A
                                            </TableCell>
                                            <TableCell className="text-right font-bold">
                                                {rawReadings.filter(r => !r.is_sub_meter).reduce((sum, r) => sum + (r.energy || 0), 0).toFixed(3)} kWh
                                            </TableCell>
                                            {rawReadings[0]?.cost !== undefined && rawReadings[0]?.cost !== null && (
                                                <TableCell className="text-right text-amber-600 font-bold">
                                                    {rawReadings.filter(r => !r.is_sub_meter).reduce((sum, r) => sum + (r.cost || 0), 0).toFixed(2)} €
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </div>
                            </>
                        )
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
